import type { AppraisalFeeHistoryEntry } from '@/services/appraisalFeeComplianceService';
import { appraisalFeeBasisLabel } from '../../../../../supabase/functions/_shared/invoice-fee-display-contract';

/** The fields any saved finding (report row or Loan Center row) exposes for the audit chain. */
export interface FeeChainSource {
  outcome: string;
  reason?: string | null;
  reasonCode?: string | null;
  ruleVersion?: string;
  feeSource?: 'CD' | 'LE' | null;
  excessCents?: number | null;
  invoiceAmountStatus?: 'verified' | 'extracted' | 'unavailable';
  pending?: boolean;
  freshState?: 'none' | 'pending' | 'satisfied' | 'attention';
  retrying?: boolean;
  needsAttention?: boolean;
  renameState?: string;
  associationStatus?: 'matched' | 'waiting' | 'review' | null;
  associationReason?: string | null;
  orderNumber?: string | null;
  checkedAt?: string | null;
  evaluatedAt?: string | null;
  freshRequestedAt?: string | null;
  freshExtractionSkipped?: boolean;
  extractedAt?: string | null;
}

export type FeeChainState = 'done' | 'active' | 'broken' | 'skipped' | 'waiting';
export interface FeeChainLink {
  key: 'read' | 'check' | 'name';
  label: string;
  state: FeeChainState;
  /** Plain-language status for the link; the next action when it is broken. */
  detail: string;
  at: string | null;
}
export interface FeeChain {
  kind: 'manual' | 'scheduled' | 'none';
  startedAt: string | null;
  finishedAt: string | null;
  busy: boolean;
  links: FeeChainLink[];
  summary: string;
}

const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const pacificTime = new Intl.DateTimeFormat('en-US', {
  month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/Los_Angeles'
});
const pacificDay = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'America/Los_Angeles' });

export function feeTime(value: string | null | undefined): string {
  return value ? pacificTime.format(new Date(value)) : '';
}
export function feeDay(value: string | null | undefined): string {
  return value ? pacificDay.format(new Date(value)) : '';
}
function parse(value: string | null | undefined): number | null {
  const time = value ? Date.parse(value) : Number.NaN;
  return Number.isFinite(time) ? time : null;
}

export const AWAITING_DISCLOSURE = 'awaiting_disclosure';

export function feeOutcomeSummary(row: Pick<FeeChainSource, 'outcome' | 'reason' | 'reasonCode' | 'ruleVersion' | 'feeSource' | 'excessCents' | 'invoiceAmountStatus'>): string {
  const basis = appraisalFeeBasisLabel(row.ruleVersion, row.feeSource).replace(/^./, value => value.toLowerCase());
  switch (row.outcome) {
    case 'overage':
      return row.excessCents === null || row.excessCents === undefined
        ? `Over the ${basis}.`
        : `Over the ${basis} by ${money.format(row.excessCents / 100)}.`;
    case 'passed': return `Within the ${basis}.`;
    case 'waiting': return row.reasonCode === AWAITING_DISCLOSURE ? 'Awaiting disclosure.' : 'Not checked yet.';
    case 'review': return row.reason || 'Needs review.';
    case 'excluded': return row.reason || 'Not part of the appraisal fee comparison.';
    case 'duplicate': return 'A copy of an invoice already checked.';
    case 'superseded': return 'Replaced by a revised invoice.';
    default: return 'Not checked yet.';
  }
}

/** Read invoice → Check fee → Name file, derived only from saved display fields. */
export function feeAuditChain(row: FeeChainSource): FeeChain {
  const requested = parse(row.freshRequestedAt);
  const lastCheckAt = row.checkedAt || row.evaluatedAt || null;
  const lastCheck = parse(lastCheckAt);
  const extracted = parse(row.extractedAt);
  const manual = requested !== null;
  const fresh = row.freshState || 'none';
  // A manual rerun is outstanding until a check lands after its request time.
  const manualOutstanding = manual && (lastCheck === null || lastCheck < requested);
  const firstCheckOutstanding = !manual && lastCheck === null && Boolean(row.pending);
  const checkDone = lastCheck !== null && !manualOutstanding && fresh !== 'attention';

  const read: FeeChainLink = { key: 'read', label: 'Read invoice', state: 'done', detail: '', at: row.extractedAt || null };
  if (fresh === 'pending') {
    read.state = 'active';
    read.detail = 'Reading the PDF again with OCR.';
  } else if (fresh === 'attention' && (manualOutstanding || lastCheck === null)) {
    read.state = 'broken';
    read.detail = 'The fresh read did not finish. Run the audit again; if it stops here twice, the file needs a new capture.';
  } else if (row.freshExtractionSkipped) {
    read.state = manualOutstanding ? 'active' : 'done';
    read.detail = manualOutstanding ? 'Reading again from the saved text.' : 'Read again from the saved text';
    read.at = manualOutstanding ? null : lastCheckAt;
  } else if (extracted !== null) {
    read.detail = manual && requested !== null && extracted >= requested ? `Fresh read ${feeTime(row.extractedAt)}.` : `Saved text from ${feeTime(row.extractedAt)}.`;
  } else {
    read.detail = 'Saved text.';
  }

  const check: FeeChainLink = { key: 'check', label: 'Check fee', state: 'waiting', detail: 'Waits for the read.', at: null };
  if (read.state === 'broken') {
    check.detail = 'Skipped until the invoice is read.';
    check.state = 'skipped';
  } else if (row.retrying) {
    check.state = 'active';
    check.detail = 'Encompass did not answer. Retrying automatically; the last saved amounts stay shown.';
  } else if (checkDone && row.reasonCode === AWAITING_DISCLOSURE) {
    check.detail = 'Awaiting disclosure. No CD or LE appraisal fee has been disclosed yet, and no discrepancy email is sent.';
  } else if (checkDone || (lastCheck !== null && fresh === 'attention')) {
    check.state = 'done';
    check.at = lastCheckAt;
    check.detail = `Live fees compared ${feeTime(lastCheckAt)}.`;
  } else if (read.state === 'done' && (manualOutstanding || firstCheckOutstanding)) {
    check.state = 'active';
    check.detail = 'Comparing the invoice with live Encompass fees.';
  }

  const name: FeeChainLink = { key: 'name', label: 'Name file', state: 'waiting', detail: 'Waits for the fee check.', at: null };
  if (check.state === 'done') {
    const verified = row.outcome === 'passed' || row.outcome === 'overage';
    if (row.renameState === 'confirmed') {
      name.state = 'done';
      name.detail = 'Renamed in Encompass.';
    } else if (row.renameState === 'pending') {
      name.state = 'active';
      name.detail = 'Writing the new file name to Encompass.';
    } else if (row.renameState === 'review') {
      name.state = 'broken';
      name.detail = 'The name could not be confirmed. Check the file name in Encompass, then run the audit again.';
    } else if (!verified) {
      name.state = 'skipped';
      name.detail = 'Not renamed: only a verified invoice is renamed.';
    } else if (!row.orderNumber) {
      name.state = 'skipped';
      name.detail = 'Not renamed: the invoice shows no order number.';
    } else {
      name.state = 'skipped';
      name.detail = 'Not renamed: naming is off for this loan.';
    }
  } else if (check.state === 'skipped') {
    name.state = 'skipped';
    name.detail = 'Skipped.';
  }

  const links = [read, check, name];
  const busy = links.some(link => link.state === 'active');
  return {
    kind: manual ? 'manual' : lastCheck !== null ? 'scheduled' : 'none',
    startedAt: manual ? row.freshRequestedAt || null : lastCheckAt,
    finishedAt: check.state === 'done' ? lastCheckAt : null,
    busy,
    links,
    summary: busy ? (read.state === 'active' ? 'Reading invoices…' : 'Checking fees…') : feeOutcomeSummary(row)
  };
}

/** One sentence when a watched rerun settles, named after the button that started it. */
export function rerunOutcomeMessage(loanNumber: string, rows: Array<Pick<FeeChainSource, 'outcome' | 'excessCents' | 'needsAttention' | 'reasonCode'>>): string {
  if (rows.some(row => row.needsAttention)) {
    return `Audit stopped for ${loanNumber}. Open History to see which step failed.`;
  }
  const overages = rows.filter(row => row.outcome === 'overage');
  if (overages.length) {
    const excess = overages.reduce((sum, row) => sum + (row.excessCents ?? 0), 0);
    return `Audit finished for ${loanNumber}. Fee overages total ${money.format(excess / 100)}. See each finding's comparison basis.`;
  }
  if (rows.length && rows.every(row => row.reasonCode === AWAITING_DISCLOSURE)) {
    return `Audit finished for ${loanNumber}. Awaiting disclosure.`;
  }
  if (rows.length) {
    return `Audit finished for ${loanNumber}. The invoice still needs review.`;
  }
  return `Audit finished for ${loanNumber}. No fee discrepancies remain.`;
}

/** Chain input from a saved version, used when the live row is missing or older than the ledger. */
export function chainSourceFromHistory(entry: AppraisalFeeHistoryEntry): FeeChainSource {
  return {
    outcome: entry.outcome, reason: entry.reason, reasonCode: entry.reasonCode, ruleVersion: entry.ruleVersion, feeSource: entry.feeSource ?? null,
    excessCents: entry.excessCents, invoiceAmountStatus: entry.invoiceAmountStatus,
    pending: false, freshState: 'none', retrying: false, needsAttention: false, renameState: entry.renameState,
    associationStatus: entry.associationStatus ?? null, associationReason: entry.associationReason ?? null, orderNumber: entry.orderNumber,
    checkedAt: entry.checkedAt, evaluatedAt: entry.evaluatedAt, freshRequestedAt: entry.freshRequestedAt,
    freshExtractionSkipped: entry.freshExtractionSkipped, extractedAt: entry.attachmentExtractedAt
  };
}

export interface FeeHistoryGroup {
  id: string;
  newest: AppraisalFeeHistoryEntry;
  oldest: AppraisalFeeHistoryEntry;
  /** Number of saved versions folded into this group. */
  count: number;
  /** True when the newest version in the group came from a manual rerun. */
  manual: boolean;
}

/** Two versions fold only when nothing a processor would act on differs. */
function groupKey(entry: AppraisalFeeHistoryEntry): string {
  return JSON.stringify([entry.outcome, entry.reasonCode, entry.reason, entry.invoiceCents, entry.invoiceAmountStatus, entry.feeCents,
    entry.feeSource ?? null, entry.excessCents, entry.ruleVersion, entry.readerVersion, entry.vendor, entry.invoiceNumber, entry.orderNumber,
    entry.associationStatus ?? null, entry.associationReason ?? null, entry.identityNote ?? null, entry.renameState, entry.renameTitle,
    entry.cures?.requiredCents ?? null, entry.cures?.appliedCents ?? null, entry.attachmentActive]);
}

function manualVersion(entry: AppraisalFeeHistoryEntry, older: AppraisalFeeHistoryEntry | undefined): boolean {
  const requested = parse(entry.freshRequestedAt);
  const created = parse(entry.createdAt);
  if (requested === null || created === null || created < requested) {
    return false;
  }
  const previous = parse(older?.createdAt);
  return previous === null || previous < requested;
}

/** Newest first; consecutive versions with identical findings fold into one group. */
export function collapseFeeHistory(entries: AppraisalFeeHistoryEntry[], attachmentId?: string | null): FeeHistoryGroup[] {
  const scoped = entries
    .filter(entry => !attachmentId || entry.attachmentId === attachmentId)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt) || a.id.localeCompare(b.id));
  const groups: FeeHistoryGroup[] = [];
  scoped.forEach((entry, index) => {
    const manual = manualVersion(entry, scoped[index + 1]);
    const last = groups[groups.length - 1];
    if (last && !manual && !last.manual && groupKey(last.oldest) === groupKey(entry)) {
      last.oldest = entry;
      last.count += 1;
      return;
    }
    groups.push({ id: entry.id, newest: entry, oldest: entry, count: 1, manual });
  });
  return groups;
}
